import React, { useEffect, useCallback, useState } from 'react';
import { VStack, View, Box } from 'native-base';
import { ActivityIndicator } from 'react-native';

// Assets & Icons
import { theme } from '@breakroom-nx/assets';

// Gifted Chat
import { GiftedChat } from 'react-native-gifted-chat';

// Components
import {
  renderSend,
  renderComposer,
  renderInputToolbar,
  renderMessageText,
  renderAvatar,
  renderSystemMessage,
  renderDay,
  renderBubble,
  renderLoadEarlier,
  scrollToBottomComponent,
  renderLoading,
  ErrorAndRefetch,
  ChatRoomNotification,
  ChatRoomNotificationType,
} from '../../';

// Classes & Types
import { Room, IMessageExt } from '@breakroom-nx/classes';
import { Status } from '@breakroom-nx/helpers';

// Redux
import {
  useGetCurrentUserQuery,
  useGetMessagesForRoomQuery,
  useSendMessageMutation,
  useUserJoinedChatMutation,
  useLazyGetMessagesForRoomQuery,
  useAppDispatch,
  openModal,
} from '@breakroom-nx/redux';

export interface ChatMessagesContainerProps {
  room: Room;
  isPreview: boolean;
}

const PAGE_SIZE = 5;

export function ChatMessagesContainer({
  room,
  isPreview,
}: ChatMessagesContainerProps) {
  const dispatch = useAppDispatch();
  const { data: currentUser } = useGetCurrentUserQuery();
  const [joiningRoom, setJoiningRoom] = useState<boolean>(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendMessage] = useSendMessageMutation();
  const [joinChat] = useUserJoinedChatMutation();
  const [translatingMessageId, setTranslatingMessageId] = useState<
    string | number
  >('');

  // Pagination state for older messages
  const [olderMessages, setOlderMessages] = useState<IMessageExt[]>([]);
  const [oldest, setOldest] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState(true);
  const [allMessages, setAllMessages] = useState<IMessageExt[]>([]);

  // Real-time latest messages (first page)
  const {
    data: latestMessages = [],
    isLoading: isLoadingMessages,
    error: messageQueryError,
    refetch: refetchMessages,
  } = useGetMessagesForRoomQuery(
    { roomId: room.id, limit: PAGE_SIZE },
    { skip: false }
  );

  // Fetch older messages on demand (pagination)
  const [
    fetchOlderMessages,
    { data: pagedMessages, isFetching: isFetchingEarlier },
  ] = useLazyGetMessagesForRoomQuery();

  // Only reset olderMessages when the room changes
  useEffect(() => {
    setOlderMessages([]);
    setHasMore(true);
    setOldest(undefined);
    setAllMessages([]); // <-- Reset to just latestMessages
    // console.log(
    //   `ROOM CHANGED OlderMessages Size: ${olderMessages.length} | LatestMessages Size: ${latestMessages.length} | All Messages Size: ${allMessages.length}`
    // );
  }, [room.id]);

  useEffect(() => {
    // console.log(
    //   `OlderMessages Size: ${olderMessages.length} | LatestMessages Size: ${latestMessages.length} | All Messages Size: ${allMessages.length}`
    // );
  }, [allMessages]);

  // When latestMessages change (but not room), update allMessages if no olderMessages
  useEffect(() => {
    setHasMore(latestMessages.length === PAGE_SIZE);
    if (latestMessages.length > 0) {
      setOldest(latestMessages[latestMessages.length - 1].createdAt);
    }
    if (olderMessages.length === 0) {
      setAllMessages(latestMessages);
    }
  }, [latestMessages]);

  // When pagedMessages arrive, append to olderMessages and update allMessages
  useEffect(() => {
    if (pagedMessages && pagedMessages.length > 0) {
      setOlderMessages((prev) => {
        const newOlder = [
          ...prev,
          ...pagedMessages.filter(
            (msg) =>
              !prev.some((m) => m._id === msg._id) &&
              !latestMessages.some((m) => m._id === msg._id)
          ),
        ];
        // Update allMessages to include both older and latest
        setAllMessages([...latestMessages, ...newOlder]);
        return newOlder;
      });
      setHasMore(pagedMessages.length === PAGE_SIZE);
      setOldest(pagedMessages[pagedMessages.length - 1].createdAt);
    } else if (pagedMessages && pagedMessages.length === 0) {
      setHasMore(false);
    }
  }, [pagedMessages, latestMessages]);

  // Pagination handler
  const handleLoadEarlier = useCallback(() => {
    if (!hasMore || isFetchingEarlier || allMessages.length === 0) return;
    fetchOlderMessages({
      roomId: room.id,
      limit: PAGE_SIZE,
      before: oldest,
    });
  }, [
    hasMore,
    isFetchingEarlier,
    allMessages,
    fetchOlderMessages,
    room.id,
    oldest,
  ]);

  // Handle Send
  const onSend = async (messages: IMessageExt[] = []) => {
    setText(messages[0].text);
    setSending(true);
    sendMessage({ user: currentUser, messages: messages, roomId: room.id })
      .then(() => {
        setText('');
        setSending(false);
      })
      .catch(() => {
        setSending(false);
      });
  };

  // Handle translation
  const handleTranslateMessage = async (message: IMessageExt) => {
    // console.log(`MEssage: ${message.text}`);
    setTranslatingMessageId(message._id);
    // try {
    //   const userLocale = navigator?.language?.split('-')[0] || 'en';
    //   if (!userLocale || !message.translations) return;

    //   if (!message.translations || !message.translations[userLocale]) {
    //     // Simulate async translation API call
    //     await new Promise((resolve) => setTimeout(resolve, 1000));
    //     // You would call your translation API here
    //   }

    //   const translation = message.translations[userLocale];
    //   if (!translation) return;

    //   setAllMessages((prevMessages) =>
    //     prevMessages.map((msg) =>
    //       msg._id === message._id
    //         ? { ...msg, text: translation, isTranslated: true }
    //         : msg
    //     )
    //   );
    // } finally {
    //   setTranslatingMessageId(null);
    // }
  };

  return (
    <View flex={1} justifyContent={'flex-end'}>
      <VStack height={'full'} flex={1}>
        {isLoadingMessages ? (
          <View w={'full'} h={'full'} justifyContent={'center'}>
            <Box mt={'-100px'}>
              <ActivityIndicator
                animating={true}
                color={theme.colors.surface[400]}
              />
            </Box>
          </View>
        ) : messageQueryError ? (
          <ErrorAndRefetch
            onRefetch={() => {
              refetchMessages();
              return;
            }}
            error={
              messageQueryError instanceof Error
                ? messageQueryError.message
                : 'Error loading messages, please try again.'
            }
          />
        ) : (
          <VStack height={'full'} flex={1}>
            <GiftedChat
              inverted={true}
              text={text}
              user={{
                _id: currentUser!.uid,
                name: currentUser!.username,
                avatar: currentUser!.avatar,
              }}
              messages={allMessages}
              renderDay={renderDay}
              renderSystemMessage={renderSystemMessage}
              renderBubble={(props) =>
                renderBubble({
                  ...props,
                  onTranslate: handleTranslateMessage,
                  translatingMessageId,
                })
              }
              renderMessageText={renderMessageText}
              messagesContainerStyle={{
                marginHorizontal: 10,
                paddingBottom: 20,
              }}
              onInputTextChanged={setText}
              renderComposer={(props) => {
                const newProps = { sending, ...props };
                return renderComposer(newProps);
              }}
              renderInputToolbar={(props) => {
                return isPreview || room.timeLeft() < 0
                  ? null
                  : renderInputToolbar(props);
              }}
              onSend={async (messages) => {
                await onSend(messages);
              }}
              renderSend={(props) => {
                const newProps = { sending, ...props };
                return renderSend(newProps);
              }}
              listViewProps={{
                nestedScrollEnabled: true,
              }}
              renderLoading={renderLoading}
              renderAvatarOnTop={true}
              renderAvatar={renderAvatar}
              parsePatterns={(linkStyle) => [
                {
                  pattern: /#(\w+)/,
                  style: linkStyle,
                  onPress: (tag: string) => {},
                  // console.log(`Pressed on hashtag: ${tag}`),
                },
              ]}
              alwaysShowSend
              scrollToBottom
              infiniteScroll
              scrollToBottomStyle={{
                marginRight: 20,
                backgroundColor: theme.colors.surface[200],
              }}
              renderLoadEarlier={renderLoadEarlier}
              scrollToBottomComponent={scrollToBottomComponent}
              loadEarlier={hasMore}
              onLoadEarlier={handleLoadEarlier}
              isLoadingEarlier={isFetchingEarlier}
            />
            {room.timeLeft() < 0 ? (
              <ChatRoomNotification
                notificationType={ChatRoomNotificationType.Expired}
                bgColor={'red.400'}
              />
            ) : isPreview ? (
              <ChatRoomNotification
                notificationType={ChatRoomNotificationType.Preview}
                bgColor={'rgba(66, 211, 189, 0.40)'}
                isLoading={joiningRoom}
                action={() => {
                  setJoiningRoom(true);
                  joinChat({ user: currentUser, roomId: room.id })
                    .then(async () => {
                      setJoiningRoom(false);
                      // setIsPreview(false);
                    })
                    .catch(() => {
                      setJoiningRoom(false);
                      dispatch(
                        openModal({
                          modalType: 'Alert',
                          modalProps: {
                            status: Status.error,
                            title: 'There was an error joining the chat room',
                          },
                        })
                      );
                    });
                }}
              />
            ) : null}
          </VStack>
        )}
      </VStack>
    </View>
  );
}

export default ChatMessagesContainer;
